import { Calendar, CalendarProps, dayjsLocalizer } from "react-big-calendar";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { media } from "../../theme";
import { Box, Flex } from "@mantine/core";
import CalendarTabControls from "./CalendarTabControls";
import SideBarForEmployee from "./SideBarForEmployee";
import CalendarFilters from "./CalendarFilters";
import CalendarShiftEvent from "./CalendarShiftEvent";
import { DndContext, DragEndEvent, DragOverlay } from "@dnd-kit/core";
import {
  assignEmployee,
  getAssignShifts,
  getWorkShifts,
} from "../../api/shiftAssignmentApi";
import { createNotification } from "../../utils/useNotification";
import CalendarShiftEventWeekly from "./CalendarShiftEventWeekly";
import { useNavigate } from "react-router";

const localizer = dayjsLocalizer(dayjs);

const MyCalendar = () => {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState<any>([]);
  const [events, setEvents] = useState<any>([]);
  const [selectedDepartment, setSelectedDepartment] = useState();
  const [selectedRole, setSelectedRole] = useState<any>();
  const [date, setDate] = useState<any>(dayjs());
  const [activeTab, setActiveTab] = useState("shift-plan");
  const [view, setView] = useState<any>("month");
  const [activeDraggableId, setActiveDraggableId] = useState<string | null>(null);

  const handleSelectSlot = useCallback(
    ({ start, end, action }: any) => {
      if (dayjs(start).isBefore(dayjs(), "day") && activeTab === "shift-plan") {
        createNotification({ success: false, message: "Geçmiş zamana vardiya atanamaz." });
        return;
      }
      if (activeTab === "shift-plan" && action === "click") {
        navigate("/calendar/shifts/new", {
          state: {
            selectedDay: { start, end },
            selectedRole,
            selectedDepartment,
            date: dayjs(date).format("YYYY/MM"),
          },
        });
      }
    },
    [activeTab, navigate, selectedRole, selectedDepartment, date]
  );

  const handleSelectEvent = useCallback(
    (event: any) => {
      if (activeTab === "shift-plan") {
        navigate(`/calendar/shifts/edit/${event.id}`, {
          state: {
            selectedEvent: event,
            selectedRole,
            selectedDepartment,
            date: dayjs(date).format("YYYY/MM"),
          },
        });
      }
    },
    [activeTab, navigate, selectedRole, selectedDepartment, date]
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveDraggableId(null);
    if (!over) return;

    const droppedEmployee = employees.find((emp: any) => emp.id === active.id);
    if (!droppedEmployee) return;

    const targetEvent = events.find((e: any) => e.id.toString() === over.id);
    if (!targetEvent) return;

    const existingEmployees = Array.isArray(targetEvent.employees) ? targetEvent.employees : [];
    const alreadyAssigned = existingEmployees.some((emp: any) => emp.id === droppedEmployee.id);
    const isCapacityFull = targetEvent.count && existingEmployees.length >= targetEvent.count;
    if (alreadyAssigned || isCapacityFull) return;

    try {
      const response = await assignEmployee({ shiftId: targetEvent.id, employees: [droppedEmployee.id] });
      createNotification({ success: response?.success, message: response?.message });
      if (response?.success) await getShiftsForEmployeeAssign();
    } catch (error) {}
  };

  const handleDragStart = (event: any) => setActiveDraggableId(event.active.id);

  const activeEmployee = useMemo(() => {
    if (!activeDraggableId) return null;
    return employees.find((emp: { id: string }) => emp.id === activeDraggableId);
  }, [activeDraggableId, employees]);

  const getShiftsForShiftPlan = async () => {
    try {
      const res: any = await getWorkShifts({
        departmentId: selectedDepartment,
        roleId: selectedRole?.value,
        date: dayjs(date).format("YYYY/MM"),
      });

      const shifts = res?.data?.response?.shifts || [];
      const formattedShifts = shifts.map(({ startTime, endTime, count, id, unpaidBreakMinutes, notes }: any) => ({
        id,
        count,
        startTime: dayjs(startTime).toDate(),
        endTime: dayjs(endTime).toDate(),
        unpaidBreakMinutes,
        notes,
        title: `${dayjs(startTime).format("HH:mm")} - ${dayjs(endTime).format("HH:mm")}`,
      }));

      setEvents(formattedShifts);
    } catch (error) {
      console.error("getShiftsForShiftPlan error:", error);
    }
  };

  const getShiftsForEmployeeAssign = async () => {
    try {
      const res: any = await getAssignShifts({
        departmentId: selectedDepartment,
        roleId: selectedRole?.value,
        date: dayjs(date).format("YYYY/MM"),
      });

      const shifts = res?.data?.shifts || [];
      const employeesList = res?.data?.employees || [];

      const formattedShifts = shifts.map(({ startTime, endTime, count, id, employees, unpaidBreakMinutes, notes }: any) => ({
        id,
        count,
        startTime: dayjs(startTime).toDate(),
        endTime: dayjs(endTime).toDate(),
        employees,
        unpaidBreakMinutes,
        notes,
        title: `${dayjs(startTime).format("HH:mm")} - ${dayjs(endTime).format("HH:mm")}`,
      }));

      setEvents(formattedShifts);
      setEmployees(employeesList);
    } catch (error) {
      console.error("getShiftsForEmployeeAssign error:", error);
    }
  };

  useEffect(() => {
    if (selectedDepartment && selectedRole) {
      if (activeTab === "shift-plan") {
        getShiftsForShiftPlan();
      } else {
        getShiftsForEmployeeAssign();
      }
    }
  }, [selectedRole, selectedDepartment, date, activeTab]);

  return (
    <DndContext onDragEnd={handleDragEnd} onDragStart={handleDragStart}>
      <Flex justify={"space-between"} mb={24} gap={16} wrap="wrap">
        <CalendarFilters
          view={view}
          setView={setView}
          date={date}
          setDate={setDate}
          selectedDepartment={selectedDepartment}
          setSelectedDepartment={setSelectedDepartment}
          selectedRole={selectedRole}
          setSelectedRole={setSelectedRole}
        />
        {selectedDepartment && selectedRole && (
          <CalendarTabControls setActiveTab={setActiveTab} activeTab={activeTab} />
        )}
      </Flex>

      {/*
        Takvim ızgarası dar ekranda karta dönüştürülemez (anlam gün×saat
        kesişiminde). Kendi kutusunda yatay kayar; min-genişlik olmadan
        7 sütun okunmaz hâle geliyordu.
      */}
      <CalendarScroll>
        {selectedDepartment && selectedRole && (
          <>
            <SideBarForEmployee
              employees={employees}
              isVisible={activeTab === "employee-assign"}
            />
            <StyledCalendar
              localizer={localizer}
              startAccessor="startTime"
              endAccessor="endTime"
              style={{ height: "800px", width: "100%", zIndex: 1 }}
              events={events}
              toolbar={false}
              view={view}
              onView={(newView: any) => setView(newView)}
              date={dayjs(date).toDate()}
              onNavigate={(newDate: Date) => setDate(dayjs(newDate))}
              selectable={true}
              onSelectSlot={handleSelectSlot}
              onSelectEvent={handleSelectEvent}
              components={{
                month: {
                  event: (props: any) => (
                    <CalendarShiftEvent
                      {...props}
                      activeTab={activeTab}
                      getShiftsForEmployeeAssign={getShiftsForEmployeeAssign}
                      selectedRole={selectedRole}
                    />
                  ),
                },
                week: {
                  event: (props: any) => (
                    <CalendarShiftEventWeekly
                      {...props}
                      activeTab={activeTab}
                      selectedRole={selectedRole}
                      getShiftsForEmployeeAssign={getShiftsForEmployeeAssign}
                      handleSelectEvent={handleSelectEvent}
                    />
                  ),
                },
              }}
            />
          </>
        )}
      </CalendarScroll>

      <DragOverlay zIndex={500} dropAnimation={null}>
        {activeEmployee ? (
          <div
            style={{
              height: "52px",
              width: "188px",
              display: "flex",
              alignItems: "center",
              border: "1px dashed #5C26FF",
              justifyContent: "center",
              cursor: "grab",
              backgroundColor: "rgba(255,255,255,0.6)",
            }}
          >
            {activeEmployee?.name}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default MyCalendar;

const CalendarScroll = styled.div`
  display: flex;
  flex: 1;
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  ${media.mobile} {
    > * {
      min-width: 680px;
    }
  }
`;

const StyledCalendar = styled(Calendar)<CalendarProps | any>`
  .rbc-header {
    display: flex;
    justify-content: end;
    align-items: center;
    padding-right: 5px;
    height: 44px;
    background-color: #eef2ff;
    span {
      font-size: 16px;
      font-weight: 600;
      line-height: 20px;
      color: #111827;
    }
  }
  .rbc-header:first-of-type { border-top-left-radius: 4px; }
  .rbc-header:last-of-type { border-top-right-radius: 4px; }
  .rbc-button-link { font-weight: 600; font-size: 16px; line-height: 20px; }
  .rbc-event {
    background: unset;
    box-sizing: unset;
    padding: unset;
    border-color: unset;
    &:focus { outline: unset; }
  }
  .rbc-event-label { display: none; }
  .rbc-date-cell { position: relative; }
  .rbc-row-segment { padding: 0px 1px 4px 1px; }
  .rbc-month-row { overflow: visible; }
  .rbc-today { background-color: unset; }
  .rbc-row { min-height: unset; }
  .rbc-month-view { border-radius: 4px; }
`;